<template>
  <div class="qcard" :class="{ selected, archived: !question.is_active, flagged: problem }" role="button" tabindex="0"
       :aria-label="`پرسش ${number || index + 1}: ${question.text || 'بی‌متن'}`" @click="$emit('select')" @keydown.enter.self.prevent="$emit('select')">
    <span class="grip q-grip" title="جابه‌جایی" aria-hidden="true"><VnIcon name="grip" /></span>
    <span class="num vn-num">{{ fa(number || index + 1) }}</span>
    <div class="body">
      <div class="title-row">
        <strong :class="{ placeholder: !question.text }">{{ question.text || 'متن پرسش را بنویسید' }}</strong>
        <span v-if="question.is_mandatory" class="req" title="پاسخ اجباری">*</span>
      </div>
      <div class="inputs">
        <AnswerPreview v-for="slot in question.answers" :key="slot.answer_type" :kind="kindName(slot)" :question="question" :rules="slot.rule.fields || {}" compact />
        <span v-if="!question.answers.length" class="empty-kind">نوع پاسخ تعیین نشده</span>
      </div>
      <div class="badges">
        <span v-for="slot in ruleSlots" :key="'r' + slot.answer_type" class="badge rule"><VnIcon name="shield" />{{ ruleLabel(slot) }}</span>
        <span v-if="photoCount" class="badge photo"><VnIcon name="camera" />{{ fa(photoCount) }} مرحله‌ی تصویر</span>
        <span v-if="question.has_bool_expected_value" class="badge alarm"><VnIcon name="alarm" />هشدار تخلف</span>
        <span v-if="question.score" class="badge">امتیاز {{ fa(question.score) }}</span>
        <span v-if="question.answered" class="badge data">{{ fa(question.answered) }} پاسخ ثبت‌شده</span>
        <span v-if="!question.is_active" class="badge archived-tag">بایگانی‌شده</span>
        <span v-if="problem" class="badge problem" :class="problem.level"><VnIcon name="alert" />{{ problem.message }}</span>
      </div>
    </div>
    <div class="tools" @click.stop>
      <button type="button" title="بالا" :disabled="index === 0" @click="$emit('move', -1)">▲</button>
      <button type="button" title="پایین" :disabled="last" @click="$emit('move', 1)">▼</button>
      <button type="button" title="رونوشت" @click="$emit('duplicate')"><VnIcon name="duplicate" /></button>
      <button v-if="question.is_active" type="button" class="danger" :title="question.answered ? 'بایگانی (پاسخ‌ها حفظ می‌شوند)' : 'حذف'" @click="$emit('remove')"><VnIcon :name="question.answered ? 'archive' : 'trash'" /></button>
      <button v-else type="button" title="بازگرداندن" @click="$emit('restore')"><VnIcon name="undo" /></button>
    </div>
  </div>
</template>

<script>
import VnIcon from '@/components/VnIcon/index.vue';
import AnswerPreview from './AnswerPreview.vue';
import { KINDS, fa } from '../designerModel';

export default {
  name: 'QuestionCard',
  components: { VnIcon, AnswerPreview },
  props: {
    question: { type: Object, required: true },
    index: { type: Number, required: true },
    number: { type: Number, default: 0 },
    last: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    typesById: { type: Object, required: true },
    presets: { type: Object, required: true },
    photoCount: { type: Number, default: 0 },
    problem: { type: Object, default: null },
  },
  computed: { ruleSlots() { return this.question.answers.filter(slot => slot.rule.mode !== 'none'); } },
  methods: {
    fa,
    kindName(slot) { const type = this.typesById[slot.answer_type]; return type ? type.name : ''; },
    ruleLabel(slot) {
      const kind = KINDS[this.kindName(slot)];
      if (slot.rule.mode === 'preset') return (this.presets[slot.rule.preset] && this.presets[slot.rule.preset].name) || 'قانون آماده';
      return `قانون ${kind ? kind.label : ''}`;
    },
  },
};
</script>

<style scoped>
.qcard { position: relative; display: grid; grid-template-columns: auto auto 1fr auto; gap: 10px; align-items: start; padding: 10px 10px 10px 8px;
  border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--surface); cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.qcard:hover { border-color: var(--line-strong); }
.qcard:focus-visible { outline: 3px solid var(--focus); outline-offset: 1px; }
.qcard.selected { border-color: var(--navy-900); box-shadow: 4px 4px 0 var(--teal-500); }
.qcard.archived { background: repeating-linear-gradient(135deg, var(--surface-sunken) 0 8px, var(--surface) 8px 16px); }
.qcard.archived .body { opacity: .6; }
.grip { color: var(--text-4); cursor: grab; padding-top: 4px; font-size: 14px; }
.grip:active { cursor: grabbing; }
.num { flex: none; width: 26px; height: 26px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--navy-900); color: var(--amber-500); font-size: 12px; font-weight: 800; }
.body { display: grid; gap: 8px; min-width: 0; }
.title-row { display: flex; align-items: baseline; gap: 4px; }
.title-row strong { font-size: 13.5px; font-weight: 800; color: var(--text); line-height: 1.8; }
.title-row strong.placeholder { color: var(--text-4); font-weight: 600; }
.req { color: var(--pink-700); font-weight: 900; }
.inputs { display: flex; flex-wrap: wrap; gap: 8px 14px; align-items: center; }
.empty-kind { font-size: 11.5px; color: var(--pink-700); }
.badges { display: flex; flex-wrap: wrap; gap: 4px; }
.badges:empty { display: none; }
.badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: var(--r-xs); background: var(--navy-50); color: var(--text-2); font-size: 10.5px; font-weight: 700; }
.badge .vn-icon { width: 12px; height: 12px; }
.badge.rule { background: var(--teal-50); color: var(--teal-700); }
.badge.photo { background: var(--amber-50); color: var(--amber-700); }
.badge.alarm, .badge.problem.error { background: var(--pink-50); color: var(--pink-700); }
.badge.problem.warning { background: var(--amber-50); color: var(--amber-700); }
.badge.data { background: var(--navy-100); color: var(--navy-700); }
.badge.archived-tag { background: var(--navy-900); color: #fff; }
.tools { display: flex; flex-direction: column; gap: 2px; opacity: 0; transition: opacity .15s; }
.qcard:hover .tools, .qcard.selected .tools, .qcard:focus-within .tools { opacity: 1; }
.tools button { width: 26px; height: 24px; display: grid; place-items: center; border: 0; border-radius: var(--r-xs); background: transparent; color: var(--text-3); font-size: 10px; cursor: pointer; }
.tools button .vn-icon { width: 15px; height: 15px; }
.tools button:hover:not(:disabled) { background: var(--navy-50); color: var(--navy-900); }
.tools button.danger:hover { background: var(--pink-50); color: var(--pink-700); }
.tools button:disabled { opacity: .3; cursor: default; }
@media (hover: none) { .tools { opacity: 1; } }
</style>
